// Global Instructions Rule Applied!
// Shape of GET /capital-calls/fund/:fundId/equalization-preview
// (view__fund_equalization_preview): outstanding late-closer catch-up per
// commitment, computed as target-vs-issued delta. The envelope passes
// through the RPC's shape (data + policy + totals + status messages);
// negative equalization deltas are credits to early closers.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const EqualizationPreviewRowSchema = z
  .object({
    fund_commitments__id: z.string(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__date_closed: z.string().nullish(),
    prog__is_late_closer: z.boolean().nullish(),
    prog__fee_catchup: z.number().nullish(),
    prog__equalization_delta: z.number().nullish(),
    prog__fixed_realloc_delta: z.number().nullish(),
    prog__rate_topup_delta: z.number().nullish(),
    prog__rate_realloc_delta: z.number().nullish(),
    sort_key: z.number().nullish()
  })
  .passthrough()

export const EqualizationPreviewSchema = z
  .object({
    data: z.array(EqualizationPreviewRowSchema).optional().default([]),
    late_closer_policy: z.string().nullish(),
    fee_schedule_start: z.string().nullish(),
    fee_catchup_status_message: z.string().nullish(),
    equalization_status_message: z.string().nullish(),
    totals: z
      .object({
        fee_catchup_total: z.number().nullish(),
        equalization_charge_total: z.number().nullish(),
        equalization_credit_total: z.number().nullish(),
        fixed_realloc_charge_total: z.number().nullish(),
        fixed_realloc_credit_total: z.number().nullish(),
        rate_topup_charge_total: z.number().nullish(),
        rate_topup_trueup_total: z.number().nullish(),
        rate_realloc_charge_total: z.number().nullish(),
        rate_realloc_credit_total: z.number().nullish()
      })
      .passthrough()
      .nullish(),
    groups: z
      .array(
        z
          .object({
            item_type_id: z.string().nullish(),
            item_type_label: z.string().nullish(),
            spec: z.string().nullish(),
            line_total: z.number().nullish(),
            source_lines: z.number().nullish()
          })
          .passthrough()
      )
      .optional()
      .default([])
  })
  .passthrough()

export type EqualizationPreviewRow = z.infer<typeof EqualizationPreviewRowSchema>
export type EqualizationPreview = z.infer<typeof EqualizationPreviewSchema>

export const validateEqualizationPreview = (data: unknown): EqualizationPreview =>
  validateWithZodSchema(EqualizationPreviewSchema, data, 'equalization preview')
